import React, { useState, useEffect } from 'react';
import { Autocomplete, Button, FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import StyledModal from '../../../Components/GenericModal/StyledModal';
import { styles } from '../../styles';
import ModalFooterButtons from '../../../Components/GenericModal/ModalFooterButtons';
import { TQuestion } from '../../types';
import { fieldTypeOptions, fieldTypeValues } from '../../config';
import { DebouncedFormTextField } from '../../../Components/CustomUIElements/FormTextField';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import { classes } from '../../../Job/Applications/styles';
import { theme } from '../../../../ThemeContext/ThemeObject';
import { useFieldChoiceList } from '../../../hooks/useFieldChoiceList';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import SortableChoice from './SortableChoice';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Api from '../../API';
import { IApplicantQuestion } from '../../../EditJob/types';
import GenericDialog from '../../../Components/Modals/GenericDialog';
import QuestionEditor from '../../../EditJob/Questions/Modals/QuestionEditor';

export const getKeyByValue = (
  object: Record<string, string>,
  value: string
): string | undefined => {
  return Object.keys(object).find((key) => object[key] === value);
};

export default function NewQuestion({
  modalOpen,
  onClose,
  selectedQuestion,
  editingQuestion,
  cloningQuestion,
  setSnackbar,
  addingToLibrary
}: {
  modalOpen: boolean;
  onClose: () => void;
  selectedQuestion: TQuestion | IApplicantQuestion | null;
  editingQuestion?: boolean;
  cloningQuestion?: boolean;
  setSnackbar: ({
    message,
    state
  }: {
    message: string;
    state: 'success' | 'warning' | 'error';
  }) => void;
  addingToLibrary?: boolean;
}) {
  const queryClient = useQueryClient();
  const [questionName, setQuestionName] = useState<string>('');
  const [fieldType, setFieldType] = useState<string | null>(null);
  const [question, setQuestion] = useState<string>('');
  const [required, setRequired] = useState<boolean>(false);
  const [rateable, setRateable] = useState<boolean>(false);
  const [confidential, setConfidential] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const { choiceList, setChoiceList, addChoice, removeChoice, updateChoice, switchChoices } =
    useFieldChoiceList();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over) {
      switchChoices(active.id, over.id);
    }
  }

  const handleChoiceInput = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target;
    updateChoice(value, index);
  };

  const validateInputs = () => {
    const errors: Record<string, string> = {};
    if (!questionName) {
      errors.questionName = 'Question name is required';
    } else {
      if (!editingQuestion && validateQuestionNameData?.error) {
        errors.questionName = 'Question name already exists';
      }
    }
    if (!fieldType) {
      errors.fieldType = 'Question type is required';
    }
    if (!question) {
      errors.question = 'Question is required';
    }
    if (fieldType && ['Select', 'Rank', 'Checkbox', 'Radio Button'].includes(fieldType)) {
      choiceList.map((choice) => {
        if (!choice.name) {
          errors.choices = 'All field choices must each have a value';
        }
      });
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const { data: validateQuestionNameData } = useQuery({
    queryKey: [questionName],
    queryFn: async () => {
      const { res } = await Api.validateQuestionName({ library_field_name: questionName });
      return res;
    }
  });

  const { mutate: submit, isLoading: submitting } = useMutation({
    mutationFn: async () => {
      let attributes;
      if (fieldType && ['Select', 'Rank', 'Checkbox', 'Radio Button'].includes(fieldType)) {
        choiceList.map((choice, index) => {
          choice.position = index;
          if (choice.id.toString().length == 13) {
            choice.id = '';
          }
        });
        const jsonString = JSON.stringify(Object.assign({}, choiceList));
        attributes = JSON.parse(jsonString);
      }
      const params = {
        library_field_name: questionName,
        title: question,
        rateable: rateable,
        required: required,
        confidential: confidential,
        field_type: fieldTypeValues[fieldType as keyof typeof fieldTypeValues],
        library: true,
        field_choices_attributes: attributes
      };
      if (editingQuestion && selectedQuestion) {
        await Api.updateQuestion(selectedQuestion.id, params);
      } else {
        await Api.newQuestionTemplate(params);
      }
    },
    onSuccess: () => {
      setSnackbar({
        message: `Question ${editingQuestion ? 'updated' : 'created'} successfully`,
        state: 'success' as const
      });
      onClose();
      queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (error) => {
      setSnackbar({
        message: `There was an error in ${editingQuestion ? 'updating' : 'creating'} the question, ${error}`,
        state: 'error' as const
      });
    }
  });

  useEffect(() => {
    if (selectedQuestion) {
      let fieldChoices;
      // When adding to library, we need to remove the id and _destroy attributes
      if (addingToLibrary || cloningQuestion) {
        fieldChoices = selectedQuestion.field_choices.map((choice) => ({
          id: '',
          _destroy: 0,
          position: choice.position,
          name: choice.name
        }));
      } else {
        fieldChoices = selectedQuestion.field_choices;
      }
      const libraryFieldName =
        'library_field_name' in selectedQuestion ? selectedQuestion.library_field_name : '';
      setChoiceList(fieldChoices);
      setQuestionName(cloningQuestion ? `${libraryFieldName} - copy` : libraryFieldName);
      setFieldType(getKeyByValue(fieldTypeValues, selectedQuestion.field_type) || null);
      setQuestion(selectedQuestion.title);
      setRequired(selectedQuestion.required);
      setRateable(selectedQuestion.rateable);
      setConfidential(selectedQuestion.confidential);
    }
  }, [
    selectedQuestion,
    setChoiceList,
    setQuestionName,
    setFieldType,
    setQuestion,
    setRequired,
    setRateable,
    setConfidential,
    cloningQuestion,
    addingToLibrary
  ]);

  const handleClose = () => setConfirmationModalOpen(true);

  return (
    <StyledModal
      isOpen={modalOpen}
      label="New question modal"
      handleClose={handleClose}
      styleOverrides={styles.modalStyleOverrides}
      closeIconId={`question-library-modal-close-icon`}
      modalId={`question-library-modal`}
      disableEnforceFocus
    >
      <Stack sx={styles.modalContainer}>
        <Stack sx={styles.modalTitle}>{`${editingQuestion ? 'Edit' : 'New'} question`}</Stack>
        <Stack sx={{ rowGap: 2, overflow: 'auto' }}>
          <DebouncedFormTextField
            label="Question name in library"
            defaultValue={questionName}
            onDebouncedChange={(value: string | number) => setQuestionName(value as string)}
            debounceTime={300}
            styles={{ width: '48%' }}
            required
            error={errors.questionName}
          />
          <Autocomplete
            disablePortal
            options={fieldTypeOptions}
            value={fieldType}
            sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
            slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Question type"
                placeholder="Please select"
                sx={sharedClasses.autoCompleteTextfield}
                slotProps={{ inputLabel: { shrink: true } }}
                required
                helperText={errors.fieldType}
              />
            )}
            onChange={(event, value) => setFieldType(value)}
          />
          <Stack>
            <QuestionEditor
              title="Question"
              label={question}
              setLabel={setQuestion}
              errorLabel={errors.question}
              noLeftMargin
            />
            <Stack sx={{ flexDirection: 'row', columnGap: 1 }}>
              <FormControlLabel
                sx={{ ...classes.formSwitchLabels, width: 'unset' }}
                control={
                  <Switch
                    sx={required ? classes.switchActive : classes.switch}
                    checked={required}
                    onChange={() => setRequired(!required)}
                  />
                }
                label="Required"
                labelPlacement="end"
              />
              <FormControlLabel
                sx={{ ...classes.formSwitchLabels, width: 'unset' }}
                control={
                  <Switch
                    sx={rateable ? classes.switchActive : classes.switch}
                    checked={rateable}
                    onChange={() => setRateable(!rateable)}
                  />
                }
                label="Rateable"
                labelPlacement="end"
              />
              <FormControlLabel
                sx={{ ...classes.formSwitchLabels, width: 'unset' }}
                control={
                  <Switch
                    sx={confidential ? classes.switchActive : classes.switch}
                    checked={confidential}
                    onChange={() => setConfidential(!confidential)}
                  />
                }
                label="Confidential"
                labelPlacement="end"
              />
            </Stack>
          </Stack>
          {fieldType && ['Select', 'Rank', 'Checkbox', 'Radio Button'].includes(fieldType) && (
            <>
              <Stack sx={{ flexDirection: 'row', columnGap: 2, alignItems: 'center' }}>
                <Stack sx={{ color: theme.palette.common.grey }}>Choices</Stack>
                <Button sx={sharedClasses.genericButtonSecondary} onClick={addChoice}>
                  Add choice
                </Button>
              </Stack>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={choiceList} strategy={verticalListSortingStrategy}>
                  {choiceList.map(
                    (choice, index) =>
                      !(choice?._destroy === 1) && (
                        <SortableChoice
                          key={choice.id}
                          choice={choice}
                          index={index}
                          handleChoiceInput={handleChoiceInput}
                          removeChoice={removeChoice}
                        />
                      )
                  )}
                </SortableContext>
                <Stack
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: '12px',
                    marginLeft: 2,
                    marginTop: -1
                  }}
                >
                  {errors.choices}
                </Stack>
              </DndContext>
            </>
          )}
        </Stack>
        <ModalFooterButtons
          primaryButtonText={editingQuestion ? 'Update' : 'Confirm'}
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          primaryButtonMinWidth="95px"
          primaryButtonCallback={() => validateInputs() && submit()}
          isLoading={submitting}
          primaryButtonID={`question-library-confirm-button`}
          secondaryButtonID={`question-library-cancel-button`}
        />
      </Stack>
      <GenericDialog
        isDialogOpen={confirmationModalOpen}
        setDialogOpen={setConfirmationModalOpen}
        title="Close without saving?"
        description={
          <Stack>
            <Stack>Are you sure you want to cancel?</Stack>
            <Stack>Unsaved changes will be lost.</Stack>
          </Stack>
        }
        buttonText="Yes, close"
        secondaryButtonText="Back to editing"
        url=""
        buttonCallback={() => {
          const list = [...choiceList];
          list.map((choice) => (choice._destroy = 0));
          setChoiceList(list);
          onClose();
        }}
        callbackLoading={false}
        primaryButtonId={`question-library-close-without-saving-button`}
        secondaryButtonId={`question-library-back-to-editing-button`}
      />
    </StyledModal>
  );
}
